// src/components/VerifyEmailBanner.jsx
//
// Shown to a signed-in member whose email is on the org's admin or coach roster
// but whose address is not yet verified. Staff roles unlock access to other
// members' profiles, so the server (syncMemberRole) refuses to elevate until the
// Firebase Auth token says email_verified. Members never see this.
//
// "Resend" sends the verification mail; "I've verified" refreshes the token and
// asks the server again. Until then the person is signed in as a plain member.

import React, { useState } from 'react';
import { MailCheck, RefreshCw, ShieldCheck } from 'lucide-react';
import { sendEmailVerification } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase-config';

const VerifyEmailBanner = ({ email, roleLabel = 'staff', onVerified }) => {
    const [busy, setBusy] = useState(null);
    const [note, setNote] = useState('');

    const resend = async () => {
        if (!auth.currentUser) return;
        setBusy('resend'); setNote('');
        try { await sendEmailVerification(auth.currentUser); setNote(`Sent to ${email}. Check spam if it isn't there in a minute.`); }
        catch (e) { setNote(e?.code === 'auth/too-many-requests' ? 'Too many requests. Wait a few minutes and try again.' : 'Could not send the email. Try again in a moment.'); }
        finally { setBusy(null); }
    };

    const check = async () => {
        if (!auth.currentUser) return;
        setBusy('check'); setNote('');
        try {
            await auth.currentUser.reload();
            await auth.currentUser.getIdToken(true); // refresh the token so email_verified is current
            if (!auth.currentUser.emailVerified) { setNote("Not verified yet. Open the link in the email first, then try again."); return; }
            const res = await httpsCallable(functions, 'syncMemberRole')({});
            if (res.data?.changed || ['admin', 'coach', 'super_admin'].includes(res.data?.role)) { onVerified && onVerified(res.data); }
            else setNote('Verified, but your email is no longer on the roster. Ask your organization admin.');
        } catch (e) { setNote('Could not confirm just now. Try again in a moment.'); }
        finally { setBusy(null); }
    };

    return (
        <div className="mx-6 mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col md:flex-row md:items-center gap-3 animate-in fade-in">
            <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><ShieldCheck size={18} /></div>
                <div className="min-w-0">
                    <p className="text-sm font-black text-slate-900">Verify your email to unlock your {roleLabel} tools</p>
                    <p className="text-xs text-slate-600 font-medium mt-0.5">Your address is on your organization's {roleLabel} list. Because {roleLabel} access reaches other members' profiles, we need to confirm it's really you. Open the link we sent to <span className="font-bold">{email}</span>. Until then you're signed in as a member.</p>
                    {note && <p className="text-xs font-bold text-amber-800 mt-1">{note}</p>}
                </div>
            </div>
            <div className="flex gap-2 shrink-0">
                <button type="button" onClick={resend} disabled={!!busy} className="px-3 py-2 rounded-xl text-xs font-bold bg-white border border-amber-200 text-amber-800 hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1.5"><RefreshCw size={14} className={busy === 'resend' ? 'animate-spin' : ''} /> Resend</button>
                <button type="button" onClick={check} disabled={!!busy} className="px-3 py-2 rounded-xl text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 flex items-center gap-1.5"><MailCheck size={14} /> I've verified</button>
            </div>
        </div>
    );
};

export default VerifyEmailBanner;
